import * as React from 'react';
import { styled } from '@linaria/react';

import StaticCodeSnippet from '@/components/CodeSnippet/ClientOnlySnippet';
import type {
  MultipleAnnotations,
  SingleAnnotation,
} from '@/components/CodeSnippet/CodeSnippet.types';

export const POSITION_RADIUS = 5;

interface SvgProps extends React.SVGProps<SVGSVGElement> {
  size?: number;
  defs?: React.ReactNode;
  children: React.ReactNode;
}
export function Svg({
  width,
  height,
  size = 280,
  defs,
  children,
  ...delegated
}: SvgProps) {
  const id = React.useId().replace(/:/g, '');
  const gridId = `${id}-grid`;

  return (
    <StyledSvg
      width={width ?? size}
      height={height ?? size}
      {...delegated}
    >
      <defs>
        <pattern
          id={gridId}
          width="20"
          height="20"
          patternUnits="userSpaceOnUse"
        >
          <path
            d={`
              M 20 0 L 20 20
              M 0 20 L 20 20
            `}
            fill="none"
            stroke="hsl(0 0% 80%)"
            strokeWidth="0.5"
          />
        </pattern>
        {defs}
      </defs>

      <rect
        width="100%"
        height="100%"
        fill={`url(#${gridId})`}
        fillOpacity="0.5"
      />
      {children}
    </StyledSvg>
  );
}

interface PositionDotProps extends React.SVGProps<SVGCircleElement> {
  cx: number;
  cy: number;
}
export function PositionDot({
  cx,
  cy,
  ...delegated
}: PositionDotProps) {
  return (
    <>
      <circle
        cx={cx}
        cy={cy}
        r={POSITION_RADIUS}
        fill="var(--color-background)"
        {...delegated}
      />
      <circle
        cx={cx}
        cy={cy}
        r={POSITION_RADIUS - 2}
        fill="white"
        {...delegated}
      />
    </>
  );
}

interface CodeSnippetProps
  extends React.HTMLAttributes<HTMLDivElement> {
  code: string;
  lang?: 'html' | 'css';
  highlightedLines?: SingleAnnotation | MultipleAnnotations;
}
export const CodeSnippet = React.memo(function CodeSnippet({
  code,
  lang = 'html',
  highlightedLines,
  ...delegated
}: CodeSnippetProps) {
  return (
    <CodeWrapper {...delegated}>
      <StaticCodeSnippet
        lang={lang}
        includeLoadingSpinner
        includeContentStyles={false}
        lockedToColorMode="dark"
        highlight={highlightedLines}
        code={code}
      />
    </CodeWrapper>
  );
});

export function Label({
  x,
  y,
  textAnchor,
  dominantBaseline,
  fontSize = '1rem',
  children,
  ref,
  ...delegated
}: React.SVGProps<SVGTextElement>) {
  return (
    <>
      <LabelBackground
        x={x}
        y={y}
        textAnchor={textAnchor}
        dominantBaseline={dominantBaseline}
        fontSize={fontSize}
        {...delegated}
      >
        {children}
      </LabelBackground>
      <LabelForeground
        x={x}
        y={y}
        textAnchor={textAnchor}
        dominantBaseline={dominantBaseline}
        fontSize={fontSize}
        {...delegated}
      >
        {children}
      </LabelForeground>
    </>
  );
}

export const Row = styled.div`
  display: flex;
  flex-direction: row-reverse;
  gap: 16px;
  padding-bottom: 16px;
  position: relative;
`;

export const Coord = styled.tspan`
  &[data-is-active='true'] {
    fill: var(--wham-primary-500);
    font-weight: bold;
  }
`;

const CodeWrapper = styled.div`
  --code-font-size: 0.875rem;
  --code-line-height: 1.4;
  flex: 1;
  min-width: 12.5rem;
  outline: 1px solid var(--color-gray-400);
  border-radius: 2px;
  white-space: pre;
  overflow: hidden;
`;

const StyledSvg = styled.svg`
  outline: 1px solid white;
  border-radius: 2px;
`;

export const Indicators = styled.g`
  transition: opacity 250ms;
`;

const LabelBase = styled.text`
  font-size: 1rem;
  font-family: var(--font-family-mono);
  transition: opacity 200ms;
  pointer-events: none;
  --selection-color: white;
  --selection-background: var(--color-background);

  &[data-is-hidden='true'] {
    opacity: 0;
  }
`;

const LabelForeground = styled(LabelBase)`
  fill: white;
  transition:
    opacity 200ms,
    fill 200ms;

  &[data-is-active='true'] {
    fill: var(--wham-primary-500);
    font-weight: 700;
  }
`;

const LabelBackground = styled(LabelBase)`
  fill: var(--color-background);
  stroke: var(--color-background);
  stroke-width: 5px;
  stroke-linecap: round;
  stroke-linejoin: round;
  user-select: none;
  pointer-events: none;
`;
